import React from 'react';
import classNames from 'classnames';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';

import Footer from '../Shared/Footer';
import { IAlyceCustomTheme } from '../../styles/alyceTheme';
import { IBranding } from '../../types/Branding.types';
import { getBranding } from '../../store/branding/branding.selectors';

const useStyles = makeStyles<IAlyceCustomTheme, IBranding & { backgroundUrl: string }>(({ palette }) => ({
  root: {
    position: 'relative',
    minHeight: '100%',
    backgroundColor: palette.customStyling.backgroundColor,
    backgroundSize: props => props.backgroundSize || 'auto',
    backgroundImage: props => `url(${props.backgroundUrl})`,
    minWidth: 300,
    color: palette.common.white,
  },
  message: {
    '@media (min-width: 769px)': {
      position: 'fixed',
      width: 'calc(50% - 24px)',
      height: '100%',
      padding: `60px 25px`,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    direction: 'rtl',
    textAlign: 'center',
    position: 'relative',
    width: '100%',
  },
  divider: {
    '@media (min-width: 769px)': {
      position: 'fixed',
      display: 'block',
      width: 50,
      height: '100%',
      top: 0,
      left: 'calc(50% - 25px)',
    },
    display: 'none',
  },
  dividerTop: {
    '@media (min-width: 769px)': {
      position: 'absolute',
      width: '100%',
      height: 'calc(50% - 50px)',
      top: 0,
      left: 0,
      background: palette.common.white,
    },
    '&:before': {
      content: "''",
      position: 'absolute',
      bottom: -50,
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 50px 50px 0',
      borderColor: 'transparent #fff transparent transparent',
    },
  },
  dividerBottom: {
    '@media (min-width: 769px)': {
      position: 'absolute',
      width: '100%',
      height: 'calc(50% - 50px)',
      top: 'calc(50% + 50px)',
      left: 0,
      background: palette.common.white,
    },
    '&:before': {
      content: "''",
      position: 'absolute',
      top: -50,
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '0 0 50px 50px',
      borderColor: 'transparent transparent #fff transparent',
    },
  },
  actions: {
    '@media (min-width: 769px)': {
      position: 'absolute',
      height: '100%',
      padding: '30px 70px 0 20px',
      width: 'calc(50% - 24px)',
      right: 0,
      top: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      boxShadow: 'none',
    },
    backgroundColor: palette.common.white,
    width: '100%',
    position: 'relative',
    paddingRight: 15,
    paddingLeft: 15,
    marginRight: 'auto',
    marginLeft: 'auto',
    boxShadow: '0px -4px 6px 0px rgba(0,0,0,0.3)',
  },
}));

export interface IGiftLayoutProps {
  actions: React.ReactNode;
  message: React.ReactNode;
  overrideClasses?: Record<string, string>;
}

const GiftLayout = ({ actions, message, overrideClasses = {} }: IGiftLayoutProps): JSX.Element => {
  const customStyling = useSelector(getBranding);
  const backgroundUrl =
    customStyling.backgroundImageUrl ||
    `${window.APP_CONFIG.apiHost}/svg/background.svg?color=${customStyling.tertiaryColor.substring(1)}`;
  const classes = useStyles({ ...customStyling, backgroundUrl });

  return (
    <Box className={classes.root}>
      <Box className={classNames(classes.message, overrideClasses.message)}>{message}</Box>
      <Box className={classes.divider}>
        <div className={classes.dividerTop} />
        <div className={classes.dividerBottom} />
      </Box>
      <Box className={classes.actions}>
        {actions}
        <Footer />
      </Box>
    </Box>
  );
};

export default GiftLayout;
